import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { 
  CheckCircle, 
  XCircle, 
  RefreshCcw, 
  MessageSquare, 
  ChevronLeft, 
  ChevronRight,
  Clock,
  Star,
  Loader2,
  AlertTriangle,
  Image as ImageIcon,
  Maximize2,
  Download,
  Send,
  MapPin,
  User,
  Columns,
  GitBranch,
  Sparkles,
  Droplets,
  Zap,
  Mountain,
  Gem,
  Phone,
  Mail,
  Calendar,
  Shield,
  Award,
  Eye,
  Edit3,
  X,
  ArrowRight,
  Building2,
  Layers,
  Heart
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ClientProposalPage - PREMIUM LUXURY EDITION
 * ==========================================
 * High-end architectural decision experience.
 * Not admin UI - this is the final client-facing experience.
 * 
 * Dark cinematic luxury aesthetic.
 * Mobile-first responsive design.
 */
export default function ClientProposalPage() {
  const { token } = useParams();
  
  // Data states
  const [proposal, setProposal] = useState(null);
  const [renders, setRenders] = useState([]);
  const [advisor, setAdvisor] = useState(null);
  const [materialDNA, setMaterialDNA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI states
  const [currentRenderIndex, setCurrentRenderIndex] = useState(0);
  const [showBefore, setShowBefore] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [compareRenderIndex, setCompareRenderIndex] = useState(null);
  
  // Action states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDecisionModal, setShowDecisionModal] = useState(null); // 'approve', 'reject', 'revision'
  const [feedback, setFeedback] = useState('');
  const [rating, setRating] = useState(0);
  
  // Visual comment system
  const [showCommentMode, setShowCommentMode] = useState(false);
  const [commentMarkers, setCommentMarkers] = useState([]);
  const [activeMarker, setActiveMarker] = useState(null);
  const [markerComment, setMarkerComment] = useState('');
  const renderImageRef = useRef(null);
  
  // Success state
  const [decisionComplete, setDecisionComplete] = useState(false);
  const [decisionType, setDecisionType] = useState(null);

  useEffect(() => {
    fetchProposal();
  }, [token]);

  const fetchProposal = async () => {
    try {
      setLoading(true);
      
      // Fetch proposal with renders
      const response = await fetch(`${API_URL}/api/client/proposal/${token}`);
      
      if (!response.ok) {
        if (response.status === 404) throw new Error('Proposta non trovata o link scaduto');
        if (response.status === 410) throw new Error('Il link della proposta è scaduto');
        throw new Error('Errore nel caricamento della proposta');
      }
      
      const data = await response.json();
      setProposal(data.proposal);
      
      // Map renders from SRE format
      const mappedRenders = (data.renders || []).map(r => ({
        ...r,
        before_url: r.source_image_url || r.before_url,
        after_url: r.render_url || r.after_url,
        version: r.version_number || 1,
        version_type: r.version_type || 'draft'
      }));
      setRenders(mappedRenders);
      
      // Fetch advisor if assigned
      if (data.proposal?.advisor_id) {
        try {
          const advisorRes = await fetch(`${API_URL}/api/ail/advisors/${data.proposal.advisor_id}`);
          if (advisorRes.ok) {
            const advisorData = await advisorRes.json();
            setAdvisor(advisorData.advisor);
          }
        } catch (e) {
          console.log('Advisor fetch optional:', e);
        }
      }
      
      // Fetch material DNA for first render
      if (mappedRenders.length > 0 && mappedRenders[0].material_id) {
        try {
          const dnaRes = await fetch(`${API_URL}/api/materials/${mappedRenders[0].material_id}`);
          if (dnaRes.ok) {
            const dnaData = await dnaRes.json();
            setMaterialDNA(dnaData.dna || dnaData.material_dna);
          }
        } catch (e) {
          console.log('DNA fetch optional:', e);
        }
      }
      
      // Mark as viewed
      try {
        await fetch(`${API_URL}/api/proposals/${data.proposal.id}/mark-viewed`, { method: 'POST' });
      } catch (e) {}
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle image click for visual comments
  const handleImageClick = useCallback((e) => {
    if (!showCommentMode) return;
    
    const rect = renderImageRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    const newMarker = {
      id: Date.now(),
      x,
      y,
      comment: '',
      render_index: currentRenderIndex
    };
    
    setActiveMarker(newMarker);
    setMarkerComment('');
  }, [showCommentMode, currentRenderIndex]);

  const saveMarkerComment = async () => {
    if (!activeMarker || !markerComment.trim()) return;
    
    const updatedMarker = { ...activeMarker, comment: markerComment };
    setCommentMarkers(prev => [...prev, updatedMarker]);
    
    // Persist to backend
    try {
      await fetch(`${API_URL}/api/proposals/${proposal.id}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: markerComment,
          render_id: renders[currentRenderIndex]?.id,
          position_x: activeMarker.x,
          position_y: activeMarker.y
        })
      });
      toast.success('Nota salvata');
    } catch (err) {
      console.error('Comment save error:', err);
    }
    
    setActiveMarker(null);
    setMarkerComment('');
    setShowCommentMode(false);
  };

  // Decision handlers
  const handleDecision = async (type) => {
    setIsSubmitting(true);
    
    try {
      const endpoint = type === 'approve' 
        ? `/api/proposals/${proposal.id}/approve`
        : type === 'reject'
        ? `/api/proposals/${proposal.id}/reject`
        : `/api/proposals/${proposal.id}/request-revision`;
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          overall_comment: feedback,
          rating: rating > 0 ? rating : undefined,
          visual_comments: commentMarkers.filter(m => m.render_index === currentRenderIndex)
        })
      });
      
      if (!response.ok) throw new Error('Errore nella richiesta');
      
      setDecisionComplete(true);
      setDecisionType(type);
      setShowDecisionModal(null);
      
      // Update proposal status
      setProposal(prev => ({ 
        ...prev, 
        status: type === 'approve' ? 'approved' : type === 'reject' ? 'rejected' : 'revision_requested'
      }));
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentRender = renders[currentRenderIndex];
  const compareRender = compareRenderIndex !== null ? renders[compareRenderIndex] : null;
  const canTakeAction = proposal?.status === 'sent' || proposal?.status === 'viewed';

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-2 border-[#B08B5C] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
          <p className="text-zinc-400 text-lg font-light tracking-wide">Caricamento proposta...</p>
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-500/10 border-2 border-red-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-light text-white mb-3">Proposta Non Disponibile</h1>
          <p className="text-zinc-400">{error}</p>
        </div>
      </div>
    );
  }

  // Decision Complete Screen
  if (decisionComplete) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="text-center max-w-lg">
          {decisionType === 'approve' ? (
            <>
              <div className="w-24 h-24 bg-gradient-to-br from-[#B08B5C] to-[#C8A36A] rounded-full flex items-center justify-center mx-auto mb-8 animate-scale-in">
                <CheckCircle className="w-12 h-12 text-black" />
              </div>
              <h1 className="text-3xl font-light text-white mb-4">Proposta Approvata</h1>
              <p className="text-zinc-400 text-lg mb-8">
                Grazie per la tua conferma. Il tuo designer è stato notificato e ti contatterà presto per i prossimi passi.
              </p>
            </>
          ) : decisionType === 'reject' ? (
            <>
              <div className="w-24 h-24 bg-zinc-800 border-2 border-zinc-600 rounded-full flex items-center justify-center mx-auto mb-8">
                <XCircle className="w-12 h-12 text-zinc-400" />
              </div>
              <h1 className="text-3xl font-light text-white mb-4">Feedback Inviato</h1>
              <p className="text-zinc-400 text-lg mb-8">
                Il tuo feedback è stato registrato. Il designer valuterà le tue osservazioni.
              </p>
            </>
          ) : (
            <>
              <div className="w-24 h-24 bg-amber-500/10 border-2 border-amber-500/30 rounded-full flex items-center justify-center mx-auto mb-8">
                <RefreshCcw className="w-12 h-12 text-amber-400" />
              </div>
              <h1 className="text-3xl font-light text-white mb-4">Revisione Richiesta</h1>
              <p className="text-zinc-400 text-lg mb-8">
                La tua richiesta di revisione è stata inviata. Riceverai una nuova proposta aggiornata.
              </p>
            </>
          )}
          
          <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800/50">
            <p className="text-sm text-zinc-500 mb-2">Riferimento proposta</p>
            <p className="text-white font-medium">{proposal?.title}</p>
            {proposal?.project_name && (
              <p className="text-zinc-400 text-sm mt-1">{proposal.project_name}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Toaster position="top-center" theme="dark" />
      
      {/* Luxury Header */}
      <header className="border-b border-zinc-800/30 bg-gradient-to-b from-[#0A0A0B] to-[#050505] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-light tracking-wide">{proposal?.title}</h1>
              <p className="text-sm text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
                <span>{proposal?.project_name}</span>
                {proposal?.project_type && (
                  <>
                    <span className="w-1 h-1 rounded-full bg-zinc-600" />
                    <span className="capitalize">{proposal.project_type}</span>
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3 sm:gap-4">
              <StatusBadge status={proposal?.status} />
              {proposal?.expires_at && (
                <div className="hidden sm:flex items-center gap-2 text-zinc-500 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>Valida fino al {new Date(proposal.expires_at).toLocaleDateString('it-IT')}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
          
          {/* Main Content - Render Viewer */}
          <div className="lg:col-span-8 space-y-6">
            
            {/* Version Tabs */}
            {renders.length > 1 && (
              <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {renders.map((render, i) => (
                  <button
                    key={render.id || i}
                    onClick={() => {
                      if (compareMode) {
                        setCompareRenderIndex(i);
                      } else {
                        setCurrentRenderIndex(i);
                      }
                    }}
                    className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      (!compareMode && i === currentRenderIndex) || (compareMode && i === compareRenderIndex)
                        ? 'bg-[#B08B5C] text-black'
                        : i === currentRenderIndex && compareMode
                        ? 'bg-zinc-700 text-white'
                        : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-700/50'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <GitBranch className="w-3.5 h-3.5" />
                      v{render.version || i + 1}
                      {render.version_type === 'alternative' && ' Alt'}
                    </span>
                  </button>
                ))}
                
                {renders.length > 1 && (
                  <button
                    onClick={() => {
                      setCompareMode(!compareMode);
                      if (!compareMode && renders.length > 1) {
                        setCompareRenderIndex(currentRenderIndex === 0 ? 1 : 0);
                      }
                    }}
                    className={`flex-shrink-0 ml-2 px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                      compareMode 
                        ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                        : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-700/50'
                    }`}
                  >
                    <Columns className="w-4 h-4" />
                    Confronta
                  </button>
                )}
              </div>
            )}

            {/* Render Display */}
            {renders.length > 0 ? (
              <div className={`${compareMode ? 'grid grid-cols-2 gap-4' : ''}`}>
                
                {/* Main/Left Render */}
                <div 
                  ref={compareMode ? null : renderImageRef}
                  className="relative aspect-[4/3] sm:aspect-[16/10] bg-zinc-900 rounded-xl overflow-hidden group cursor-pointer border border-zinc-800/30"
                  onClick={compareMode ? () => {} : handleImageClick}
                >
                  <img 
                    src={showBefore ? currentRender?.before_url : currentRender?.after_url}
                    alt={showBefore ? "Prima" : "Dopo"}
                    className="w-full h-full object-cover transition-all duration-700"
                    onError={(e) => { e.target.src = '/placeholder-render.jpg'; }}
                  />
                  
                  {/* Comment markers */}
                  {!compareMode && commentMarkers
                    .filter(m => m.render_index === currentRenderIndex)
                    .map((marker, i) => (
                      <div
                        key={marker.id}
                        className="absolute w-8 h-8 -ml-4 -mt-4 bg-[#B08B5C] rounded-full flex items-center justify-center text-black text-sm font-bold cursor-pointer hover:scale-110 transition-transform shadow-lg"
                        style={{ left: `${marker.x}%`, top: `${marker.y}%` }}
                        onClick={(e) => { e.stopPropagation(); toast.info(marker.comment); }}
                      >
                        {i + 1}
                      </div>
                    ))
                  }
                  
                  {/* Active marker input */}
                  {activeMarker && (
                    <div 
                      className="absolute z-20"
                      style={{ left: `${activeMarker.x}%`, top: `${activeMarker.y}%` }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="w-6 h-6 -ml-3 -mt-3 bg-[#B08B5C] rounded-full animate-pulse" />
                      <div className="absolute left-4 top-0 bg-zinc-900 border border-zinc-700 rounded-lg p-3 w-64 shadow-2xl">
                        <textarea
                          value={markerComment}
                          onChange={(e) => setMarkerComment(e.target.value)}
                          placeholder="Es: Preferisco venature più calme qui..."
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-sm text-white placeholder:text-zinc-500 resize-none focus:outline-none focus:border-[#B08B5C]"
                          rows={2}
                          autoFocus
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => setActiveMarker(null)}
                            className="flex-1 py-1.5 text-xs bg-zinc-700 rounded-lg hover:bg-zinc-600"
                          >
                            Annulla
                          </button>
                          <button
                            onClick={saveMarkerComment}
                            disabled={!markerComment.trim()}
                            className="flex-1 py-1.5 text-xs bg-[#B08B5C] text-black rounded-lg hover:brightness-110 disabled:opacity-50"
                          >
                            Salva
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Before/After Toggle */}
                  {!compareMode && (
                    <div className="absolute bottom-4 left-4 flex gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowBefore(false); }}
                        className={`px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm transition-all ${
                          !showBefore 
                            ? 'bg-[#B08B5C] text-black' 
                            : 'bg-black/60 text-white hover:bg-black/80'
                        }`}
                      >
                        Dopo
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowBefore(true); }}
                        className={`px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm transition-all ${
                          showBefore 
                            ? 'bg-[#B08B5C] text-black' 
                            : 'bg-black/60 text-white hover:bg-black/80'
                        }`}
                      >
                        Prima
                      </button>
                    </div>
                  )}
                  
                  {/* Material Label */}
                  <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-sm px-4 py-2 rounded-lg">
                    <p className="text-sm font-medium">{currentRender?.material_name}</p>
                    <p className="text-xs text-zinc-400 capitalize">{currentRender?.surface_type || 'Countertop'}</p>
                  </div>
                  
                  {/* Quality Badge */}
                  {currentRender?.quality_classification && (
                    <div className={`absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-medium backdrop-blur-sm ${
                      currentRender.quality_classification === 'believable' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                      currentRender.quality_classification === 'acceptable' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                      'bg-zinc-800 text-zinc-400'
                    }`}>
                      {currentRender.quality_classification === 'believable' ? 'Alta Qualità' :
                       currentRender.quality_classification === 'acceptable' ? 'Buona Qualità' : 'Anteprima'}
                    </div>
                  )}
                  
                  {/* Comment Mode Indicator */}
                  {showCommentMode && !compareMode && (
                    <div className="absolute inset-0 bg-[#B08B5C]/10 border-4 border-[#B08B5C]/50 rounded-xl pointer-events-none">
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/80 backdrop-blur-sm px-6 py-3 rounded-full">
                        <p className="text-sm text-[#B08B5C] font-medium">Clicca per aggiungere una nota</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Fullscreen */}
                  {!compareMode && (
                    <button 
                      className="absolute bottom-4 right-4 p-2.5 bg-black/60 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => { e.stopPropagation(); setShowFullscreen(true); }}
                    >
                      <Maximize2 className="w-5 h-5" />
                    </button>
                  )}
                  
                  {/* Version Label in Compare Mode */}
                  {compareMode && (
                    <div className="absolute bottom-4 left-4 bg-zinc-700/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-medium">
                      Versione {currentRender?.version || currentRenderIndex + 1}
                    </div>
                  )}
                </div>
                
                {/* Compare Right Render */}
                {compareMode && compareRender && (
                  <div className="relative aspect-[4/3] sm:aspect-[16/10] bg-zinc-900 rounded-xl overflow-hidden border border-zinc-800/30">
                    <img 
                      src={compareRender?.after_url || compareRender?.render_url}
                      alt="Confronto"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-sm px-4 py-2 rounded-lg">
                      <p className="text-sm font-medium">{compareRender?.material_name}</p>
                    </div>
                    <div className="absolute bottom-4 left-4 bg-[#B08B5C]/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-medium text-black">
                      Versione {compareRender?.version || (compareRenderIndex + 1)}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="aspect-[16/10] bg-zinc-900 rounded-xl flex items-center justify-center border border-zinc-800/30">
                <div className="text-center text-zinc-500">
                  <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Nessun render disponibile</p>
                </div>
              </div>
            )}

            {/* Comment Mode Toggle */}
            {canTakeAction && renders.length > 0 && (
              <button
                onClick={() => setShowCommentMode(!showCommentMode)}
                className={`w-full py-3 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  showCommentMode
                    ? 'bg-[#B08B5C] text-black'
                    : 'bg-zinc-800/50 border border-zinc-700 text-zinc-300 hover:bg-zinc-700/50'
                }`}
              >
                <Edit3 className="w-4 h-4" />
                {showCommentMode ? 'Esci dalla modalità note' : 'Aggiungi note visive sul render'}
              </button>
            )}

            {/* Visual Comments List */}
            {commentMarkers.filter(m => m.render_index === currentRenderIndex).length > 0 && (
              <div className="bg-zinc-900/30 rounded-xl p-5 border border-zinc-800/30">
                <h3 className="text-sm font-medium text-zinc-400 mb-4 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Le tue note ({commentMarkers.filter(m => m.render_index === currentRenderIndex).length})
                </h3>
                <div className="space-y-3">
                  {commentMarkers
                    .filter(m => m.render_index === currentRenderIndex)
                    .map((marker, i) => (
                      <div key={marker.id} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-[#B08B5C] rounded-full flex items-center justify-center text-black text-xs font-bold flex-shrink-0">
                          {i + 1}
                        </div>
                        <p className="text-sm text-zinc-300">{marker.comment}</p>
                      </div>
                    ))
                  }
                </div>
              </div>
            )}

            {/* Version Change Info */}
            {currentRender?.render_notes && (
              <div className="bg-purple-500/5 rounded-xl p-5 border border-purple-500/20">
                <h3 className="text-sm font-medium text-purple-400 mb-2 flex items-center gap-2">
                  <GitBranch className="w-4 h-4" />
                  Perché questa versione
                </h3>
                <p className="text-sm text-zinc-300">{currentRender.render_notes}</p>
              </div>
            )}

            {/* Description */}
            {proposal?.description && (
              <div className="bg-zinc-900/30 rounded-xl p-6 border border-zinc-800/30">
                <h3 className="text-sm font-medium text-zinc-400 mb-3">Descrizione del Progetto</h3>
                <p className="text-zinc-200 leading-relaxed">{proposal.description}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            
            {/* Material Confidence Panel */}
            {currentRender && (
              <div className="bg-gradient-to-br from-zinc-900/80 to-zinc-900/30 rounded-xl p-6 border border-zinc-800/30">
                <h3 className="text-sm font-medium text-zinc-400 mb-4 flex items-center gap-2">
                  <Gem className="w-4 h-4 text-[#B08B5C]" />
                  Caratteristiche del Materiale
                </h3>
                
                <div className="space-y-4">
                  {/* Material Name */}
                  <div className="flex items-center gap-3 pb-4 border-b border-zinc-800/50">
                    <div className="w-12 h-12 rounded-lg bg-zinc-800 flex items-center justify-center">
                      <Mountain className="w-6 h-6 text-[#B08B5C]" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{currentRender.material_name}</p>
                      <p className="text-xs text-zinc-500 capitalize">{currentRender.material_category || 'Pietra Naturale'}</p>
                    </div>
                  </div>
                  
                  {/* DNA Metrics in Human Language */}
                  <DNAMetricRow 
                    icon={Sparkles} 
                    label="Eleganza" 
                    value={materialDNA?.luxury_index || currentRender.realism_score / 10}
                    description="Raffinatezza percepita"
                  />
                  <DNAMetricRow 
                    icon={Heart} 
                    label="Calore" 
                    value={materialDNA?.warmth || 6}
                    description="Tonalità calda/fredda"
                  />
                  <DNAMetricRow 
                    icon={Layers} 
                    label="Movimento" 
                    value={materialDNA?.movement || 5}
                    description="Intensità delle venature"
                  />
                  <DNAMetricRow 
                    icon={Zap} 
                    label="Energia Visiva" 
                    value={materialDNA?.visual_energy || 4}
                    description="Impatto visivo complessivo"
                  />
                  <DNAMetricRow 
                    icon={Eye} 
                    label="Calma Visiva" 
                    value={10 - (materialDNA?.movement || 5)}
                    description="Serenità della superficie"
                  />
                  
                  {/* Suitability Tags */}
                  <div className="pt-3 border-t border-zinc-800/50">
                    <p className="text-xs text-zinc-500 mb-2">Adatto per</p>
                    <div className="flex flex-wrap gap-2">
                      {(materialDNA?.waterfall_suitable !== false || currentRender.layout_type === 'waterfall') && (
                        <span className="px-2 py-1 bg-zinc-800/50 rounded-full text-xs text-zinc-300">Waterfall</span>
                      )}
                      {(materialDNA?.bookmatch_suitable !== false) && (
                        <span className="px-2 py-1 bg-zinc-800/50 rounded-full text-xs text-zinc-300">Bookmatch</span>
                      )}
                      <span className="px-2 py-1 bg-zinc-800/50 rounded-full text-xs text-zinc-300">Cucina</span>
                      <span className="px-2 py-1 bg-zinc-800/50 rounded-full text-xs text-zinc-300">Bagno</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Advisor Trust Panel */}
            {advisor && (
              <div className="bg-zinc-900/30 rounded-xl p-6 border border-zinc-800/30">
                <h3 className="text-sm font-medium text-zinc-400 mb-4 flex items-center gap-2">
                  <Award className="w-4 h-4 text-[#B08B5C]" />
                  Il Tuo Consulente
                </h3>
                
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#B08B5C] to-[#C8A36A] flex items-center justify-center">
                    <User className="w-7 h-7 text-black" />
                  </div>
                  <div>
                    <p className="font-medium text-white">{advisor.full_name}</p>
                    <p className="text-xs text-zinc-500">{advisor.title}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <div className={`w-2 h-2 rounded-full ${
                        advisor.online_status === 'online' ? 'bg-green-400' : 'bg-zinc-500'
                      }`} />
                      <span className="text-xs text-zinc-400">
                        {advisor.online_status === 'online' ? 'Disponibile ora' : 'Su appuntamento'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-zinc-400">
                    <MapPin className="w-4 h-4" />
                    <span>{advisor.city}, Italia</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Clock className="w-4 h-4" />
                    <span>Risposta media: ~{advisor.response_time_hours || 4} ore</span>
                  </div>
                </div>
              </div>
            )}

            {/* Rating Section */}
            {canTakeAction && (
              <div className="bg-zinc-900/30 rounded-xl p-6 border border-zinc-800/30">
                <h3 className="text-sm font-medium text-zinc-400 mb-4">La tua impressione</h3>
                <div className="flex justify-center gap-2 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      className="p-1 transition-transform hover:scale-110"
                    >
                      <Star 
                        className={`w-8 h-8 transition-colors ${
                          star <= rating 
                            ? 'fill-[#C8A36A] text-[#C8A36A]' 
                            : 'text-zinc-700 hover:text-zinc-500'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Note o preferenze particolari..."
                  className="w-full bg-zinc-800/50 border border-zinc-700 rounded-xl p-4 text-sm text-white placeholder:text-zinc-500 resize-none focus:outline-none focus:border-[#B08B5C] transition-colors"
                  rows={3}
                />
              </div>
            )}

            {/* Decision Buttons */}
            {canTakeAction && (
              <div className="space-y-3">
                <button
                  onClick={() => setShowDecisionModal('approve')}
                  disabled={isSubmitting}
                  className="w-full py-4 bg-gradient-to-r from-[#B08B5C] to-[#C8A36A] text-black font-semibold rounded-xl hover:brightness-110 transition-all flex items-center justify-center gap-3 disabled:opacity-50 text-base"
                >
                  <CheckCircle className="w-5 h-5" />
                  Approva Proposta
                </button>
                
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setShowDecisionModal('revision')}
                    disabled={isSubmitting}
                    className="py-3.5 bg-zinc-800/80 border border-zinc-700 rounded-xl hover:bg-zinc-700/80 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                  >
                    <RefreshCcw className="w-4 h-4" />
                    Revisione
                  </button>
                  
                  <button
                    onClick={() => setShowDecisionModal('reject')}
                    disabled={isSubmitting}
                    className="py-3.5 bg-zinc-800/80 border border-red-500/20 text-red-400 rounded-xl hover:bg-red-500/10 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                  >
                    <XCircle className="w-4 h-4" />
                    Rifiuta
                  </button>
                </div>
              </div>
            )}

            {/* Already Decided */}
            {!canTakeAction && proposal?.status && (
              <DecisionDisplay status={proposal.status} respondedAt={proposal.responded_at} />
            )}

            {/* Proposal Validity */}
            <div className="bg-zinc-900/30 rounded-xl p-5 border border-zinc-800/30">
              <div className="flex items-center gap-3 text-zinc-400">
                <Shield className="w-5 h-5 text-[#B08B5C]" />
                <div>
                  <p className="text-sm font-medium text-zinc-300">Proposta verificata</p>
                  <p className="text-xs text-zinc-500">
                    Creata il {new Date(proposal?.created_at).toLocaleDateString('it-IT', { 
                      day: 'numeric', month: 'long', year: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* Value */}
            {proposal?.total_value && (
              <div className="bg-gradient-to-br from-[#B08B5C]/10 to-transparent rounded-xl p-6 border border-[#B08B5C]/20">
                <p className="text-sm text-zinc-400 mb-1">Valore Stimato</p>
                <p className="text-3xl font-light text-[#C8A36A]">
                  {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(proposal.total_value)}
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Fullscreen Modal */}
      {showFullscreen && currentRender && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 sm:p-8"
          onClick={() => setShowFullscreen(false)}
        >
          <button 
            className="absolute top-4 right-4 p-3 text-white/60 hover:text-white bg-white/10 rounded-full"
            onClick={() => setShowFullscreen(false)}
          >
            <X className="w-6 h-6" />
          </button>
          <img 
            src={showBefore ? currentRender.before_url : currentRender.after_url}
            alt="Fullscreen"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-4">
            <button
              onClick={(e) => { e.stopPropagation(); setShowBefore(false); }}
              className={`px-6 py-3 rounded-full font-medium transition-all ${
                !showBefore ? 'bg-[#B08B5C] text-black' : 'bg-white/10 text-white'
              }`}
            >
              Dopo
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setShowBefore(true); }}
              className={`px-6 py-3 rounded-full font-medium transition-all ${
                showBefore ? 'bg-[#B08B5C] text-black' : 'bg-white/10 text-white'
              }`}
            >
              Prima
            </button>
          </div>
        </div>
      )}

      {/* Decision Modals */}
      {showDecisionModal && (
        <DecisionModal
          type={showDecisionModal}
          feedback={feedback}
          isSubmitting={isSubmitting}
          onClose={() => setShowDecisionModal(null)}
          onConfirm={() => handleDecision(showDecisionModal)}
          commentCount={commentMarkers.filter(m => m.render_index === currentRenderIndex).length}
        />
      )}
    </div>
  );
}


// =====================================================
// SUB-COMPONENTS
// =====================================================

function StatusBadge({ status }) {
  const config = {
    draft: { label: 'Bozza', bg: 'bg-zinc-700', text: 'text-zinc-300' },
    sent: { label: 'In Attesa', bg: 'bg-[#B08B5C]/20', text: 'text-[#B08B5C]', border: 'border-[#B08B5C]/30' },
    viewed: { label: 'In Revisione', bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
    approved: { label: 'Approvata', bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
    rejected: { label: 'Rifiutata', bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
    revision_requested: { label: 'Revisione Richiesta', bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
    expired: { label: 'Scaduta', bg: 'bg-zinc-700', text: 'text-zinc-400' }
  };
  
  const c = config[status] || config.draft;
  
  return (
    <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${c.bg} ${c.text} ${c.border ? `border ${c.border}` : ''}`}>
      {c.label}
    </span>
  );
}


function DNAMetricRow({ icon: Icon, label, value, description }) {
  const numValue = parseFloat(value) || 0;
  const percentage = Math.min(100, numValue * 10);
  
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-zinc-500" />
          <span className="text-sm text-zinc-300">{label}</span>
        </div>
        <span className="text-xs text-zinc-500">{Math.round(numValue)}/10</span>
      </div>
      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-[#B08B5C] to-[#C8A36A] rounded-full transition-all duration-500"
          style={{ width: `${percentage}%` }}
        />
      </div>
      {description && (
        <p className="text-xs text-zinc-600">{description}</p>
      )}
    </div>
  );
}


function DecisionDisplay({ status, respondedAt }) {
  const config = {
    approved: {
      icon: CheckCircle,
      title: 'Proposta Approvata',
      bg: 'bg-green-500/5',
      border: 'border-green-500/20',
      iconColor: 'text-green-500'
    },
    rejected: {
      icon: XCircle,
      title: 'Proposta Rifiutata',
      bg: 'bg-red-500/5',
      border: 'border-red-500/20',
      iconColor: 'text-red-500'
    },
    revision_requested: {
      icon: RefreshCcw,
      title: 'Revisione Richiesta',
      bg: 'bg-amber-500/5',
      border: 'border-amber-500/20',
      iconColor: 'text-amber-500'
    },
    draft: {
      icon: Clock,
      title: 'In Lavorazione',
      bg: 'bg-zinc-800/50',
      border: 'border-zinc-700',
      iconColor: 'text-zinc-500'
    }
  };
  
  const c = config[status] || config.draft;
  const Icon = c.icon;
  
  return (
    <div className={`p-6 rounded-xl border ${c.bg} ${c.border}`}>
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-full ${c.bg} flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${c.iconColor}`} />
        </div>
        <div>
          <p className="font-medium text-white">{c.title}</p>
          {respondedAt && (
            <p className="text-sm text-zinc-500">
              {new Date(respondedAt).toLocaleDateString('it-IT', {
                day: 'numeric', month: 'long', year: 'numeric'
              })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}


function DecisionModal({ type, feedback, isSubmitting, onClose, onConfirm, commentCount }) {
  const config = {
    approve: {
      icon: CheckCircle,
      title: 'Conferma Approvazione',
      description: 'Stai per approvare questa proposta. Il tuo designer sarà notificato immediatamente e procederà con la realizzazione.',
      iconBg: 'bg-gradient-to-br from-[#B08B5C] to-[#C8A36A]',
      iconColor: 'text-black',
      confirmBg: 'bg-gradient-to-r from-[#B08B5C] to-[#C8A36A]',
      confirmText: 'text-black',
      confirmLabel: 'Approva'
    },
    reject: {
      icon: XCircle,
      title: 'Invia Feedback',
      description: 'Il tuo feedback aiuterà il designer a comprendere le tue preferenze per future proposte.',
      iconBg: 'bg-red-500/10',
      iconColor: 'text-red-400',
      confirmBg: 'bg-red-500/20 border border-red-500/30',
      confirmText: 'text-red-400',
      confirmLabel: 'Invia Feedback'
    },
    revision: {
      icon: RefreshCcw,
      title: 'Richiedi Revisione',
      description: 'Richiedi modifiche al materiale o al render. Il designer preparerà una nuova versione basata sulle tue indicazioni.',
      iconBg: 'bg-amber-500/10',
      iconColor: 'text-amber-400',
      confirmBg: 'bg-amber-500/20 border border-amber-500/30',
      confirmText: 'text-amber-400',
      confirmLabel: 'Richiedi Revisione'
    }
  };
  
  const c = config[type];
  const Icon = c.icon;
  
  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-[#0F0F10] border border-zinc-800 rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`w-16 h-16 ${c.iconBg} rounded-full flex items-center justify-center mx-auto mb-6`}>
          <Icon className={`w-8 h-8 ${c.iconColor}`} />
        </div>
        
        <h3 className="text-xl font-semibold text-white text-center mb-2">{c.title}</h3>
        <p className="text-zinc-400 text-center mb-6 text-sm leading-relaxed">{c.description}</p>
        
        {commentCount > 0 && (
          <div className="bg-zinc-800/50 rounded-lg p-3 mb-6 text-center">
            <p className="text-sm text-zinc-300">
              <span className="text-[#B08B5C] font-medium">{commentCount}</span> note visive saranno incluse
            </p>
          </div>
        )}
        
        {feedback && (
          <div className="bg-zinc-800/50 rounded-lg p-4 mb-6">
            <p className="text-xs text-zinc-500 mb-1">Il tuo messaggio</p>
            <p className="text-sm text-zinc-300">{feedback}</p>
          </div>
        )}
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-zinc-800 text-white rounded-xl hover:bg-zinc-700 transition-colors font-medium"
          >
            Annulla
          </button>
          <button
            onClick={onConfirm}
            disabled={isSubmitting}
            className={`flex-1 py-3 ${c.confirmBg} ${c.confirmText} font-semibold rounded-xl hover:brightness-110 transition-all flex items-center justify-center gap-2 disabled:opacity-50`}
          >
            {isSubmitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Icon className="w-5 h-5" />
                {c.confirmLabel}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
